import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// InputBar — поле ввода-композер (§0b «Поле ввода», FR-MSG/FR-MEDIA/FR-VOICE).
// Слева: скрепка (вложения) + эмодзи (открывает пикер — слот/колбэк, нативные emoji, FR-MSG-03);
// центр: текстовое поле (авторост); справа: МОРФИНГ микрофон↔отправка — поле пустое → микрофон
// (зажать-записать/увести-отменить, жесты в onMicPress*; саму запись делает voice-recorder), есть
// текст → кнопка «отправить» (плавно превращается). Над полем — плашка reply/edit с кнопкой отмены.
// Сам InputBar НЕ пишет звук и НЕ шлёт сеть — только UI и жесты (инв.4). Презентационный.
//
// Из форка «Наш.Чат» 03.11.2026 (ENT-1): на тач-устройстве без мыши Enter экранной клавиатуры — НОВАЯ
// СТРОКА (отправка — кнопкой), на desktop — как раньше: Enter отправляет, Shift+Enter — перенос.
// Ctrl/Cmd+Enter отправляет везде. Enter во время IME-композиции не трогаем. Поле растёт по
// содержимому до FIELD_MAX_HEIGHT_PX (раньше rows=1 без автороста — абзацы уезжали внутрь поля).
import { useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { Icon } from '../primitives/Icon.js';
import { IconButton } from '../primitives/Button.js';
import { formatDuration } from '../util/format.js';
import { previewForKind } from '../util/format.js';
/** Потолок автороста поля ввода (≈6 строк текста сообщения); дальше — прокрутка внутри поля. */
export const FIELD_MAX_HEIGHT_PX = 160;
/** Основной ввод — палец (телефон/планшет без мыши): там Enter экранной клавиатуры = перенос строки. */
export function isTouchPrimaryInput() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
        return false;
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
}
export function InputBar(props) {
    const { value, onChange, onSend, onAttach, onEmoji, onPasteClipboard, mode, recording, onMicPressStart, onMicPressEnd, onMicCancel, replyingTo, onCancelReply, editing, onCancelEdit, attachments, uploads, onCancelUpload, onRetryUpload, placeholder = 'Сообщение', disabled = false, } = props;
    const micActive = useRef(false);
    const fieldRef = useRef(null);
    // Тип устройства не меняется за жизнь экрана — определяем один раз (проп главнее).
    const [touchPrimary] = useState(isTouchPrimaryInput);
    const enterSends = props.enterSends ?? !touchPrimary;
    // Есть что отправлять: текст ИЛИ готовое вложение (chip attachments или завершённая загрузка uploads).
    const hasContent = value.trim().length > 0 ||
        (attachments !== undefined && attachments.length > 0) ||
        (uploads !== undefined && uploads.some((u) => u.ready));
    const onKeyDown = (e) => {
        if (e.key !== 'Enter' || mode !== 'send')
            return;
        // Enter, подтверждающий IME-композицию (подсказки клавиатуры, азиатский ввод), — не отправка.
        if (e.nativeEvent.isComposing || e.keyCode === 229)
            return;
        // Ctrl/Cmd+Enter — отправка на любом устройстве; просто Enter — только где enterSends.
        const send = e.ctrlKey || e.metaKey || (enterSends && !e.shiftKey && !e.altKey);
        if (!send)
            return; // перенос строки — штатное поведение textarea
        e.preventDefault();
        if (hasContent)
            onSend();
    };
    const onInput = (e) => onChange(e.target.value);
    // Авторост: высота поля = высоте содержимого (до потолка), дальше — прокрутка внутри.
    const fitField = useCallback(() => {
        const el = fieldRef.current;
        if (el === null)
            return;
        el.style.height = 'auto';
        const full = el.scrollHeight;
        // Нет раскладки (скрытая панель, jsdom) — не фиксируем 0px, пусть решает rows=1.
        if (full === 0) {
            el.style.height = '';
            return;
        }
        el.style.height = `${Math.min(full, FIELD_MAX_HEIGHT_PX)}px`;
        el.style.overflowY = full > FIELD_MAX_HEIGHT_PX ? 'auto' : 'hidden';
    }, []);
    // После записи голосового поле монтируется заново — тоже пересчитать.
    const isRecording = recording !== undefined;
    useLayoutEffect(fitField, [value, isRecording, fitField]);
    useEffect(() => {
        // Поворот/смена ширины меняет перенос строк — пересчитать.
        if (typeof window === 'undefined')
            return;
        window.addEventListener('resize', fitField);
        return () => window.removeEventListener('resize', fitField);
    }, [fitField]);
    const micDown = (e) => {
        e.preventDefault();
        micActive.current = true;
        onMicPressStart?.();
    };
    const micUp = () => {
        if (!micActive.current)
            return;
        micActive.current = false;
        onMicPressEnd?.();
    };
    const micLeave = () => {
        // увести-отменить (палец/курсор ушёл с кнопки во время записи)
        if (!micActive.current)
            return;
        micActive.current = false;
        onMicCancel?.();
    };
    const bar = {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 'var(--space-1)',
        padding: 'var(--space-2)',
        background: 'var(--color-surface)',
        borderTop: '1px solid var(--color-divider)',
    };
    const field = {
        flex: 1,
        minWidth: 0,
        maxHeight: `${FIELD_MAX_HEIGHT_PX}px`,
        // border-box: высота из scrollHeight уже включает padding — авторост не «прибавляет» его дважды
        boxSizing: 'border-box',
        resize: 'none',
        border: 'none',
        outline: 'none',
        background: 'transparent',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-family-base)',
        // тем же кеглем, что и пузырь: что набрал — то и увидят
        fontSize: 'var(--font-message)',
        lineHeight: 'var(--line-normal)',
        padding: '8px 0',
    };
    const pill = {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-3)',
        borderLeft: '3px solid var(--color-accent)',
        background: 'var(--color-bg-secondary)',
    };
    const activePill = editing ?? replyingTo;
    const pillLabel = editing ? 'Редактирование' : 'Ответ';
    const pillCancel = editing ? onCancelEdit : onCancelReply;
    return (_jsxs("div", { "data-input-bar": "", "data-mode": mode, children: [activePill ? (_jsxs("div", { "data-input-pill": "", "data-kind": editing ? 'edit' : 'reply', style: pill, children: [_jsxs("span", { style: { flex: 1, minWidth: 0 }, children: [_jsx("span", { style: {
                                    display: 'block',
                                    fontSize: 'var(--font-xs)',
                                    color: 'var(--color-accent)',
                                    fontWeight: 'var(--weight-medium)',
                                }, children: pillLabel }), _jsx("span", { style: {
                                    display: 'block',
                                    fontSize: 'var(--font-sm)',
                                    color: 'var(--color-text-muted)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }, children: previewForKind(activePill.kind, activePill.preview) })] }), _jsx(IconButton, { icon: "close", label: "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C", onClick: () => pillCancel?.(), size: 18 })] })) : null, attachments && attachments.length > 0 ? (_jsx("div", { "data-input-attachments": "", style: {
                    display: 'flex',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-3)',
                    overflowX: 'auto',
                }, children: attachments.map((a) => (_jsxs("span", { "data-pending-attachment": a.kind, style: {
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-pill)',
                        background: 'var(--color-surface-hover)',
                        fontSize: 'var(--font-xs)',
                        whiteSpace: 'nowrap',
                    }, children: [_jsx(Icon, { name: a.kind === 'image' ? 'image' : a.kind === 'video' ? 'video' : 'file', size: 14 }), a.filename] }, a.id))) })) : null, uploads && uploads.length > 0 ? (_jsx("div", { "data-input-uploads": "", style: { display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', padding: 'var(--space-2) var(--space-3)' }, children: uploads.map((u) => {
                    // ready/error главнее phase; без phase незавершённая загрузка — «идёт» (прежнее поведение).
                    const state = u.error
                        ? 'error'
                        : u.ready
                            ? 'ready'
                            : (u.phase ?? 'uploading');
                    // шиммер «идёт загрузка» — только когда байты реально идут (не в очереди и не в ожидании сети)
                    const uploading = state === 'uploading';
                    const pct = Math.round(Math.min(1, Math.max(0, u.progress)) * 100);
                    const statusText = state === 'error'
                        ? 'ошибка'
                        : state === 'ready'
                            ? 'готово'
                            : state === 'queued'
                                ? 'в очереди'
                                : state === 'waiting'
                                    ? 'ждём сеть…'
                                    : `${pct}%`;
                    return (_jsxs("div", { "data-upload-item": u.slotId, "data-upload-state": state, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }, children: [_jsx(Icon, { name: u.kind === 'image' ? 'image' : u.kind === 'video' ? 'video' : 'file', size: 16 }), _jsx("span", { style: {
                                            flex: 1,
                                            minWidth: 0,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            fontSize: 'var(--font-sm)',
                                        }, children: u.filename }), _jsx("span", { "data-upload-status": "", style: {
                                            fontSize: 'var(--font-xs)',
                                            color: u.error ? 'var(--color-danger)' : 'var(--color-text-muted)',
                                            flex: '0 0 auto',
                                        }, children: statusText }), state === 'error' && onRetryUpload ? (_jsx("button", { type: "button", "data-retry-upload": u.slotId, onClick: () => onRetryUpload(u.slotId), style: {
                                            flex: '0 0 auto',
                                            border: 'none',
                                            background: 'transparent',
                                            // зона нажатия под палец (кнопка в тесной строке плашки)
                                            padding: '6px 8px',
                                            margin: '-6px 0',
                                            color: 'var(--color-accent)',
                                            fontFamily: 'var(--font-family-base)',
                                            fontSize: 'var(--font-sm)',
                                            fontWeight: 'var(--weight-medium)',
                                            cursor: 'pointer',
                                        }, children: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C" })) : null, onCancelUpload ? (_jsx(IconButton, { icon: "close", label: "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u043A\u0443", size: 16, "data-cancel-upload": u.slotId, onClick: () => onCancelUpload(u.slotId) })) : null] }), _jsxs("div", { "data-upload-bar": "", style: {
                                    position: 'relative',
                                    height: '3px',
                                    marginTop: '4px',
                                    borderRadius: '2px',
                                    background: 'var(--color-divider)',
                                    overflow: 'hidden',
                                }, children: [_jsx("div", { style: {
                                            position: 'absolute',
                                            insetBlock: 0,
                                            left: 0,
                                            width: `${u.error ? 100 : pct}%`,
                                            background: u.error ? 'var(--color-danger)' : 'var(--color-accent)',
                                            transition: 'width var(--motion-normal) var(--ease-standard)',
                                        } }), uploading ? (_jsx("div", { "data-upload-shimmer": "", style: {
                                            position: 'absolute',
                                            insetBlock: 0,
                                            left: 0,
                                            width: '40%',
                                            background: 'linear-gradient(90deg, transparent, var(--color-accent-muted), transparent)',
                                            animation: 'tc-ds-indeterminate 1.1s var(--ease-standard) infinite',
                                        } })) : null] })] }, u.slotId));
                }) })) : null, _jsxs("div", { style: bar, children: [recording ? (_jsxs("div", { "data-recording": "", style: {
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            color: 'var(--color-danger)',
                            fontSize: 'var(--font-md)',
                            padding: '8px 0',
                        }, children: [_jsx("span", { style: {
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    background: 'var(--color-danger)',
                                    animation: 'tc-ds-pulse 1s var(--ease-standard) infinite',
                                } }), _jsx("span", { "data-recording-time": "", children: formatDuration(recording.elapsedMs) }), _jsx("span", { style: { color: 'var(--color-text-muted)', fontSize: 'var(--font-sm)' }, children: "\u043E\u0442\u043F\u0443\u0441\u0442\u0438\u0442\u0435 \u0434\u043B\u044F \u043E\u0442\u043F\u0440\u0430\u0432\u043A\u0438, \u0443\u0432\u0435\u0434\u0438\u0442\u0435 \u0434\u043B\u044F \u043E\u0442\u043C\u0435\u043D\u044B" })] })) : (_jsxs(_Fragment, { children: [_jsx(IconButton, { icon: "attach", label: "\u0412\u043B\u043E\u0436\u0435\u043D\u0438\u0435", onClick: onAttach, disabled: disabled }), onPasteClipboard ? (_jsx(IconButton, { icon: "clipboard", label: "\u0412\u0441\u0442\u0430\u0432\u0438\u0442\u044C \u0438\u0437 \u0431\u0443\u0444\u0435\u0440\u0430", "data-paste-clipboard": "", onClick: onPasteClipboard, disabled: disabled })) : null, _jsx(IconButton, { icon: "emoji", label: "\u042D\u043C\u043E\u0434\u0437\u0438", onClick: onEmoji, disabled: disabled }), _jsx("textarea", { ref: fieldRef, "data-input-field": "", "data-enter-sends": enterSends ? 'true' : 'false', rows: 1, value: value, placeholder: placeholder, disabled: disabled, 
                                // подпись клавиши Enter экранной клавиатуры: «перенос» на телефоне, «отправить» на desktop
                                enterKeyHint: enterSends ? 'send' : 'enter', onChange: onInput, onKeyDown: onKeyDown, style: field })] })), mode === 'send' ? (_jsx(IconButton, { icon: "send", label: "\u041E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u044C", "data-morph": "send", onClick: () => hasContent && onSend(), disabled: disabled || !hasContent, style: { color: 'var(--color-accent)' } })) : (_jsx("button", { type: "button", "data-morph": "record", "aria-label": "\u0417\u0430\u043F\u0438\u0441\u0430\u0442\u044C \u0433\u043E\u043B\u043E\u0441\u043E\u0432\u043E\u0435", disabled: disabled, onPointerDown: micDown, onPointerUp: micUp, onPointerLeave: micLeave, onPointerCancel: micLeave, style: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '36px',
                            height: '36px',
                            border: 'none',
                            borderRadius: 'var(--radius-control)',
                            background: 'transparent',
                            color: recording ? 'var(--color-danger)' : 'var(--color-accent)',
                            cursor: 'pointer',
                            transition: 'color var(--motion-normal) var(--ease-standard)',
                            // «зажать-записать» на touch: без touch-action:none браузер трактует удержание как
                            // скролл/жест и шлёт pointercancel → запись рвётся сразу после старта. Плюс глушим
                            // выделение/iOS-callout на самой иконке, чтобы long-press не открывал системное меню.
                            touchAction: 'none',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            WebkitTouchCallout: 'none',
                        }, children: _jsx(Icon, { name: "mic", size: 20 }) }))] })] }));
}
//# sourceMappingURL=InputBar.js.map